import {RfxStage} from '@frontend/directus';
import {
  ParticipatingToolResponse,
  TenderPhaseType,
  TenderSelection,
  TenderStatus,
} from '@frontend/shared-api';

export interface PhaseFieldMapping {
  selectionField: keyof ParticipatingToolResponse;
  statusField: keyof ParticipatingToolResponse;
}

export class TenderPhaseUtils {
  public static readonly PHASE_ORDER = [
    TenderPhaseType.RFI,
    TenderPhaseType.RFP,
    TenderPhaseType.RFD,
    TenderPhaseType.RFQ,
    TenderPhaseType.RFC,
  ] as const;

  private static readonly PHASE_RFX_STAGE_MAPPING: Record<TenderPhaseType, RfxStage> = {
    [TenderPhaseType.RFI]: 'rfi',
    [TenderPhaseType.RFP]: 'rfp',
    [TenderPhaseType.RFD]: 'rfd',
    [TenderPhaseType.RFQ]: 'rfq',
    [TenderPhaseType.RFC]: 'rfc',
  };

  public static readonly LAST_TENDER_PHASE =
    TenderPhaseUtils.PHASE_ORDER[TenderPhaseUtils.PHASE_ORDER.length - 1];

  public static resolveRfxStage(phase: TenderPhaseType): RfxStage {
    return this.PHASE_RFX_STAGE_MAPPING[phase];
  }

  private static readonly PHASE_FIELD_MAPPING: Record<TenderPhaseType, PhaseFieldMapping> = {
    [TenderPhaseType.RFI]: {selectionField: 'rfiSelection', statusField: 'rfiStatus'},
    [TenderPhaseType.RFP]: {selectionField: 'rfpSelection', statusField: 'rfpStatus'},
    [TenderPhaseType.RFD]: {selectionField: 'rfdSelection', statusField: 'rfdStatus'},
    [TenderPhaseType.RFQ]: {selectionField: 'rfqSelection', statusField: 'rfqStatus'},
    [TenderPhaseType.RFC]: {selectionField: 'rfcSelection', statusField: 'rfcStatus'},
  };

  public static mapPhaseStringToEnum(phase: string | null): TenderPhaseType | null {
    if (!phase) return null;

    const upperPhase = phase.toUpperCase();

    if (Object.values(TenderPhaseType).includes(upperPhase as TenderPhaseType)) {
      return upperPhase as TenderPhaseType;
    }

    return null;
  }

  public static getApiResponseFieldValues(
    response: ParticipatingToolResponse,
    phase: TenderPhaseType,
  ): {selection: TenderSelection | undefined; status: TenderStatus | undefined} {
    const {selectionField, statusField} = this.PHASE_FIELD_MAPPING[phase];

    return {
      selection: response[selectionField] as TenderSelection | undefined,
      status: response[statusField] as TenderStatus | undefined,
    };
  }

  public static getPhaseOrderIndex(phase: TenderPhaseType): number {
    return this.PHASE_ORDER.indexOf(phase);
  }

  public static getNextPhase(currentPhase: TenderPhaseType): TenderPhaseType | null {
    const currentIndex = this.getPhaseOrderIndex(currentPhase);
    return this.PHASE_ORDER[currentIndex + 1];
  }
}
